import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { useQuery } from '@tanstack/react-query';
import { ProductService } from 'proto/gen/ts/shirt/v1/product_pb';
import { Link } from 'react-router-dom';
import NotifySignup from '../components/products/NotifySignup';
import { formatPrice } from '../utils/format';
import { shopCatalogRow, TEST_IDS } from '../utils/testIds';
import { transport } from '../utils/transport';
const productClient = createClient(ProductService, transport);
const FRAUNCES_DISPLAY_BOLD = {
    fontWeight: 700,
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 144, "SOFT" 50'
};
const FRAUNCES_DISPLAY_ITALIC = {
    fontWeight: 500,
    fontStyle: 'italic',
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 144, "SOFT" 100'
};
const FRAUNCES_MEDIUM = {
    fontWeight: 500,
    letterSpacing: '-0.02em',
    fontVariationSettings: '"opsz" 96, "SOFT" 50'
};
/**
 * Editorial catalog page. Two segments — Available (numbered, priced, clickable to PDP)
 * and In development (numbered separately, with inline notify-me signup that stays on
 * the row). Reads like a magazine TOC, not a Shopify grid.
 *
 * Catalog is intentionally small. The empty state is the same as the homepage's catalog
 * section: a quiet "back soon" rather than a forced grid.
 */ export default function ShopPage() {
    const { data, isLoading } = useQuery({
        queryKey: [
            'products',
            'shop'
        ],
        queryFn: ()=>productClient.listProducts({
                featuredOnly: false,
                limit: 0
            })
    });
    const products = data?.products ?? [];
    const available = products.filter((p)=>p.lifecycle !== 'in_development');
    const inDevelopment = products.filter((p)=>p.lifecycle === 'in_development');
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("section", {
                className: "relative bg-bone-50 overflow-hidden",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": true,
                        className: "absolute inset-0 pointer-events-none",
                        style: {
                            background: 'radial-gradient(ellipse 50% 40% at 85% 20%, rgba(162, 88, 31, 0.12), transparent 65%)'
                        }
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 lg:pt-28 lg:pb-16",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center gap-3 mb-8",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "h-px w-10 bg-clay-500",
                                        "aria-hidden": true
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "font-mono text-[10px] uppercase tracking-[0.35em] text-clay-600 font-medium",
                                        children: "The catalog"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("h1", {
                                className: "font-display text-obsidian-900 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl",
                                style: FRAUNCES_DISPLAY_BOLD,
                                children: [
                                    "Every essential,",
                                    /*#__PURE__*/ _jsx("br", {}),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-clay-600",
                                        style: FRAUNCES_DISPLAY_ITALIC,
                                        children: "already shipping"
                                    }),
                                    ' ',
                                    "or in process."
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mt-8 max-w-xl text-obsidian-700 text-lg leading-[1.65]",
                                children: "We don't add to the catalog quickly. Each piece is designed, prototyped, and held to the same standard before it ships — so what you see below is either ready, or close."
                            })
                        ]
                    })
                ]
            }),
            isLoading && /*#__PURE__*/ _jsx("section", {
                className: "bg-bone-50",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "border-t border-dune-200/60",
                        children: Array.from({
                            length: 2
                        }).map((_, i)=>/*#__PURE__*/ _jsxs("div", {
                                className: "border-b border-dune-200/60 py-12 grid grid-cols-12 gap-6 items-center animate-pulse",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "col-span-1 h-12 bg-dune-200/50"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "col-span-3 aspect-[4/5] bg-dune-200/40"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "col-span-6 space-y-3",
                                        children: [
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-6 bg-dune-200/60 w-2/3"
                                            }),
                                            /*#__PURE__*/ _jsx("div", {
                                                className: "h-4 bg-dune-200/40 w-1/2"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "col-span-2 h-5 bg-dune-200/50"
                                    })
                                ]
                            }, `skel-${String(i)}`))
                    })
                })
            }),
            !isLoading && available.length === 0 && inDevelopment.length === 0 && /*#__PURE__*/ _jsx("section", {
                className: "bg-bone-50",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "max-w-3xl mx-auto px-6 py-20 text-center",
                    children: /*#__PURE__*/ _jsx("p", {
                        className: "font-mono text-[11px] uppercase tracking-[0.3em] text-obsidian-500",
                        children: "Nothing in the catalog yet — back soon."
                    })
                })
            }),
            !isLoading && available.length > 0 && /*#__PURE__*/ _jsx("section", {
                "data-testid": TEST_IDS.shopAvailableSection,
                className: "bg-bone-50",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-20 lg:pb-28",
                    children: [
                        /*#__PURE__*/ _jsx(SegmentHeader, {
                            eyebrow: "Available now",
                            title: "Now shipping.",
                            count: available.length
                        }),
                        /*#__PURE__*/ _jsx("ol", {
                            className: "border-t border-dune-200/60",
                            children: available.map((p, idx)=>/*#__PURE__*/ _jsx("li", {
                                    "data-testid": shopCatalogRow(p.slug),
                                    className: "border-b border-dune-200/60",
                                    children: /*#__PURE__*/ _jsx(CatalogRow, {
                                        product: p,
                                        index: idx
                                    })
                                }, p.id))
                        })
                    ]
                })
            }),
            !isLoading && inDevelopment.length > 0 && /*#__PURE__*/ _jsx("section", {
                "data-testid": TEST_IDS.shopInDevelopmentSection,
                className: "bg-bone-100 border-t border-dune-200/60",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28",
                    children: [
                        /*#__PURE__*/ _jsx(SegmentHeader, {
                            eyebrow: "In development",
                            title: "What's coming next.",
                            count: inDevelopment.length,
                            accent: "ember"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "mt-6 max-w-xl text-obsidian-700 text-lg leading-[1.65]",
                            children: "These pieces are in design or prototype. The form, finish, and feel are still being tuned — drop your email to be the first to know when one is ready."
                        }),
                        /*#__PURE__*/ _jsx("ol", {
                            className: "mt-12 border-t border-dune-200/60",
                            children: inDevelopment.map((p, idx)=>/*#__PURE__*/ _jsx("li", {
                                    "data-testid": shopCatalogRow(p.slug),
                                    className: "border-b border-dune-200/60",
                                    children: /*#__PURE__*/ _jsx(DevelopmentRow, {
                                        product: p,
                                        index: idx
                                    })
                                }, p.id))
                        })
                    ]
                })
            })
        ]
    });
}
function SegmentHeader({ eyebrow, title, count, accent = 'clay' }) {
    const accentText = accent === 'ember' ? 'text-ember-500' : 'text-clay-600';
    const accentBar = accent === 'ember' ? 'bg-ember-400' : 'bg-clay-500';
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-end justify-between gap-6 mb-10",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-3 mb-5",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `h-px w-8 ${accentBar}`,
                                "aria-hidden": true
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: `font-mono text-[10px] uppercase tracking-[0.35em] font-medium ${accentText}`,
                                children: eyebrow
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        className: "font-display text-obsidian-900 text-3xl sm:text-4xl lg:text-5xl leading-[0.95]",
                        style: FRAUNCES_DISPLAY_BOLD,
                        children: title
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                className: "hidden sm:block font-mono text-[11px] uppercase tracking-[0.28em] text-obsidian-500 pb-2",
                children: [
                    String(count).padStart(2, '0'),
                    " ",
                    count === 1 ? 'piece' : 'pieces'
                ]
            })
        ]
    });
}
/** A shipping product row — clickable, leads to PDP, big editorial type, price on the right. */ function CatalogRow({ product, index }) {
    return /*#__PURE__*/ _jsxs(Link, {
        to: `/shop/${product.slug}`,
        className: "group grid grid-cols-12 gap-4 lg:gap-8 items-center py-8 lg:py-12 transition-colors",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "col-span-2 lg:col-span-1 font-display text-clay-500 text-3xl lg:text-5xl group-hover:text-clay-600 transition-colors",
                style: FRAUNCES_DISPLAY_ITALIC,
                children: String(index + 1).padStart(2, '0')
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "col-span-10 lg:col-span-3",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "aspect-[4/5] bg-dune-100 overflow-hidden",
                    children: product.imageUrl ? /*#__PURE__*/ _jsx("img", {
                        src: product.imageUrl,
                        alt: product.name,
                        className: "w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: "w-full h-full flex items-center justify-center text-obsidian-400 text-xs font-mono uppercase tracking-[0.2em]",
                        children: "Image coming"
                    })
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "col-span-12 lg:col-span-6",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-display text-obsidian-900 text-3xl lg:text-5xl leading-[0.95] group-hover:text-clay-600 transition-colors",
                        style: FRAUNCES_DISPLAY_BOLD,
                        children: product.name
                    }),
                    product.description && /*#__PURE__*/ _jsx("p", {
                        className: "mt-4 text-obsidian-600 text-base lg:text-lg leading-[1.55] max-w-xl",
                        children: product.description
                    }),
                    /*#__PURE__*/ _jsxs("span", {
                        className: "mt-5 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-500 group-hover:text-clay-600 transition-colors",
                        children: [
                            "Read the story",
                            /*#__PURE__*/ _jsx("span", {
                                "aria-hidden": true,
                                className: "inline-block translate-x-0 group-hover:translate-x-1 transition-transform",
                                children: "→"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "col-span-12 lg:col-span-2 lg:text-right",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "font-display text-obsidian-900 text-2xl lg:text-3xl",
                        style: FRAUNCES_MEDIUM,
                        children: formatPrice(product.priceCents)
                    }),
                    product.inStock ? /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[10px] font-mono uppercase tracking-[0.28em] text-sage-500",
                        children: "In stock"
                    }) : product.leadTimeDays > 0 ? /*#__PURE__*/ _jsxs("p", {
                        className: "mt-2 text-[10px] font-mono uppercase tracking-[0.28em] text-ember-500",
                        children: [
                            "Ships in ",
                            product.leadTimeDays,
                            " day",
                            product.leadTimeDays === 1 ? '' : 's'
                        ]
                    }) : /*#__PURE__*/ _jsx("p", {
                        className: "mt-2 text-[10px] font-mono uppercase tracking-[0.28em] text-obsidian-400",
                        children: "Out of stock"
                    })
                ]
            })
        ]
    });
}
/** An in-development row — non-clickable name, teaser body, inline notify-me on the right. */ function DevelopmentRow({ product, index }) {
    const subscriberCount = product.notifySubscriberCount;
    return /*#__PURE__*/ _jsxs("div", {
        className: "grid grid-cols-12 gap-4 lg:gap-8 items-center py-8 lg:py-12",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "col-span-2 lg:col-span-1 font-display text-ember-400 text-3xl lg:text-5xl",
                style: FRAUNCES_DISPLAY_ITALIC,
                children: String(index + 1).padStart(2, '0')
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "col-span-10 lg:col-span-3",
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "aspect-[4/5] bg-dune-200/60 overflow-hidden relative",
                    children: [
                        product.imageUrl ? /*#__PURE__*/ _jsx("img", {
                            src: product.imageUrl,
                            alt: product.name,
                            className: "w-full h-full object-cover opacity-90"
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: "w-full h-full flex items-center justify-center text-obsidian-400 text-xs font-mono uppercase tracking-[0.2em] text-center px-4",
                            children: "Prototype phase"
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: "absolute top-3 left-3 bg-obsidian-900/85 text-bone-50 text-[9px] font-mono uppercase tracking-[0.28em] px-2 py-1",
                            children: "In development"
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "col-span-12 lg:col-span-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "font-display text-obsidian-900 text-3xl lg:text-5xl leading-[0.95]",
                        style: FRAUNCES_DISPLAY_BOLD,
                        children: product.name
                    }),
                    (product.developmentTeaser || product.description) && /*#__PURE__*/ _jsx("p", {
                        className: "mt-4 text-obsidian-600 text-base lg:text-lg leading-[1.55] max-w-md",
                        children: product.developmentTeaser || product.description
                    }),
                    /*#__PURE__*/ _jsxs(Link, {
                        to: `/shop/${product.slug}`,
                        className: "mt-5 inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.28em] text-obsidian-500 hover:text-clay-600 transition-colors",
                        children: [
                            "Read the story",
                            /*#__PURE__*/ _jsx("span", {
                                "aria-hidden": true,
                                children: "→"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "col-span-12 lg:col-span-4",
                children: [
                    /*#__PURE__*/ _jsx(NotifySignup, {
                        product: product,
                        variant: "inline",
                        tone: "dark"
                    }),
                    subscriberCount > 0 && /*#__PURE__*/ _jsxs("p", {
                        className: "mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-obsidian-500",
                        children: [
                            subscriberCount,
                            " ",
                            subscriberCount === 1 ? 'person' : 'people',
                            " on the list"
                        ]
                    })
                ]
            })
        ]
    });
}
